// Realised P&L for Polymarket US, average-cost ledger built from the venue's fill/resolution activity feed.

import type { Cash, MC, MQ } from '../core/money';
import type { PmFill, PmResolution } from './parse';

export interface PmMarketLedger {
  slug: string;
  /** Net YES position after every fill seen, MQ (signed). */
  netMq: MQ;
  /** Cost basis of that position, Cash (signed like the position). */
  costCash: Cash;
  realizedCash: Cash;
  feeCash: Cash;
  fills: PmFill[];
  lastT: number;
  /** When the market resolved, epoch ms; absent while open. */
  settledT?: number;
}

export function buildLedger(fills: PmFill[], resolutions: PmResolution[] = []): Map<string, PmMarketLedger> {
  const out = new Map<string, PmMarketLedger>();
  const get = (slug: string): PmMarketLedger => {
    let l = out.get(slug);
    if (!l) {
      l = { slug, netMq: 0, costCash: 0, realizedCash: 0, feeCash: 0, fills: [], lastT: 0 };
      out.set(slug, l);
    }
    return l;
  };
  type Ev = { t: number; fill?: PmFill; res?: PmResolution };
  const events: Ev[] = [
    ...fills.map((f) => ({ t: f.t, fill: f })),
    ...resolutions.map((r) => ({ t: r.t, res: r })),
  ].sort((a, b) => a.t - b.t);

  for (const ev of events) {
    if (ev.fill) {
      const f = ev.fill;
      const l = get(f.slug);
      l.fills.push(f);
      l.feeCash += f.feeCash;
      l.lastT = Math.max(l.lastT, f.t);
      apply(l, f.side === 'yes' ? f.mq : -f.mq, f.yesPriceMc);
    } else if (ev.res) {
      const r = ev.res;
      const l = get(r.slug);
      l.lastT = Math.max(l.lastT, r.t);
      l.settledT = r.t;
      if (r.realizedDeltaCash !== null) {
        l.realizedCash += r.realizedDeltaCash;
        l.netMq = 0;
        l.costCash = 0;
      } else if (r.settleMc !== null && l.netMq !== 0) {
        apply(l, -l.netMq, r.settleMc);
      }
    }
  }
  return out;
}

/**
 * The same ledger split by the app account each fill belongs to. A resolution's realised figure is market-wide, so it is
 * shared across the accounts holding the market in proportion to their positions; with no holder it goes to `fallback`.
 */
export function buildAccountLedgers(
  fills: PmFill[], resolutions: PmResolution[], accountOf: (f: PmFill) => string, fallback: string,
): Map<string, Map<string, PmMarketLedger>> {
  const out = new Map<string, Map<string, PmMarketLedger>>();
  const get = (acct: string, slug: string): PmMarketLedger => {
    let m = out.get(acct);
    if (!m) out.set(acct, (m = new Map()));
    let l = m.get(slug);
    if (!l) m.set(slug, (l = { slug, netMq: 0, costCash: 0, realizedCash: 0, feeCash: 0, fills: [], lastT: 0 }));
    return l;
  };
  type Ev = { t: number; fill?: PmFill; res?: PmResolution };
  const events: Ev[] = [
    ...fills.map((f) => ({ t: f.t, fill: f })),
    ...resolutions.map((r) => ({ t: r.t, res: r })),
  ].sort((a, b) => a.t - b.t);

  for (const ev of events) {
    if (ev.fill) {
      const f = ev.fill;
      const l = get(accountOf(f), f.slug);
      l.fills.push(f);
      l.feeCash += f.feeCash;
      l.lastT = Math.max(l.lastT, f.t);
      apply(l, f.side === 'yes' ? f.mq : -f.mq, f.yesPriceMc);
      continue;
    }
    const r = ev.res!;
    const holders = [...out.values()].map((m) => m.get(r.slug)).filter((l): l is PmMarketLedger => !!l && l.netMq !== 0);
    for (const l of holders) {
      l.lastT = Math.max(l.lastT, r.t);
      l.settledT = r.t;
    }
    if (r.realizedDeltaCash === null) {
      if (r.settleMc !== null) for (const l of holders) apply(l, -l.netMq, r.settleMc);
      continue;
    }
    if (!holders.length) {
      const l = get(fallback, r.slug);
      l.realizedCash += r.realizedDeltaCash;
      l.lastT = Math.max(l.lastT, r.t);
      l.settledT = r.t;
      continue;
    }
    const sameSign = holders.every((l) => Math.sign(l.netMq) === Math.sign(holders[0].netMq));
    if (!sameSign && r.settleMc !== null) {
      for (const l of holders) apply(l, -l.netMq, r.settleMc);
      continue;
    }
    // Shares by position size; the last holder takes the rounding so the total is exact.
    const weight = (l: PmMarketLedger) => Math.abs(l.netMq);
    const total = holders.reduce((s, l) => s + weight(l), 0);
    let left = r.realizedDeltaCash;
    holders.forEach((l, i) => {
      const part = i === holders.length - 1 ? left : Math.round((r.realizedDeltaCash! * weight(l)) / total);
      l.realizedCash += part;
      left -= part;
      l.netMq = 0;
      l.costCash = 0;
    });
  }
  return out;
}

/** Average-cost bookkeeping for one signed fill of `q` at YES price `px`. */
function apply(l: PmMarketLedger, q: MQ, px: MC): void {
  if (l.netMq === 0 || Math.sign(q) === Math.sign(l.netMq)) {
    l.netMq += q;
    l.costCash += q * px;
    return;
  }
  const closing = Math.sign(q) * Math.min(Math.abs(q), Math.abs(l.netMq));
  const avg = l.costCash / l.netMq;
  // Long closing at px above avg gains; short closing at px below avg gains.
  l.realizedCash += -closing * (px - avg);
  l.netMq += closing;
  l.costCash = l.netMq === 0 ? 0 : l.netMq * avg;
  const rest = q - closing;
  if (rest) {
    l.netMq += rest;
    l.costCash += rest * px;
  }
}
